import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Emit, Watch, Provide} from 'vue-property-decorator';
import { Status } from '@/models/status.model';
import EdPreviewFilePage from '@/courses/components/ed-preview-file-pages/ed-preview-file-page/ed-preview-file-page.vue';
import { PreviewPageItem } from '@/courses/interfaces/preview-page-item.interface';
import { EdPreviewFilePagesProvider } from '../ed-preview-file-pages/ed-preview-file-pages.provider';
import { SECTION_TYPES } from '@/content/values/sections';
import KEYS from '@/values/keys';

@Component({
  name: 'ed-pdf-rotate-pages',
  components: {
    EdPreviewFilePage
  }
})
export default class EdPdfRotatePages extends Vue {
  @Prop() readonly url: string;
  @Prop() readonly params: any;
  @Prop() readonly rotateDegrees: any;
  @Prop({required: false, default: true}) readonly validateForm: boolean;
  @Prop({required: false, default: false}) readonly showRotateMessage: boolean;

  @Provide()
  PreviewFilePagesProvider = new EdPreviewFilePagesProvider();

  pages: PreviewPageItem[] = [];
  rotateDegreesInfo: any = [];
  startPage: number = null;
  endPage: number = null;
  firstPagesToRender: number = 30;
  addPages: number = 2;
  renderComponent: number = 0;
  intitFunction: boolean = false;
  pdfStatus: Status = new Status();
  pagesNumberRendered: number = 0;
  pageToGo = '';
  isRendered :boolean = false;
  pagesLoaded: number = 0;
  isRotating :boolean = false;


  @Emit('onRotatePages')
  emitOnRotatePages() {
    this.isRotating = true;
    return this.rotateDegreesInfo;
  }

  /* Computed */
  get product() {
    return this.params.product;
  }

  get hasGrading() {
    return this.product?.hasGrading;
  }

  get pdfPages(): any[] {
    return this.pages.map(a => {
      return {
        ...a,
        rotateDegreesInfo: (this.rotateDegreesInfo) ? this.rotateDegreesInfo.find(e => e.page == this.pages.indexOf(a)) : null
      }
    });
  }

  get pagesInfo(): PreviewPageItem[] {
    return this.PreviewFilePagesProvider.pages;
  }

  get pagesRendered(): boolean {
    if(this.pagesInfo.length > 0) {
      return this.pagesInfo.reduce((acc, item) => {
        if(acc) {
          return { ...acc, rendered: acc.rendered && item.rendered };
        } else {
          return item;
        }
      }).rendered;
    } else {
      return true;
    }
  }

  get showRotateMessageActive() {
    return (this.showRotateMessage && this.isRotating);
  }

  get canSaveByButton() {
    return this.showRotateMessage && ((this.rotateDegreesInfo || []).length > 0);
  }

  get numberPages(): number {
    return this.PreviewFilePagesProvider.totalPages;
  }

  get showDisclaimer(): boolean {
    return this.params?.type !== SECTION_TYPES.SPLIT_SOLUTION_PDF.code && !this.isTemplateCourse;
  }

  get selectedCourse() {
    return this.$store.state.CourseStore.courses.content.find(
      (course: any) => course.id === this.params.courseId
    );
  }

  get isTemplateCourse(): boolean {
    const courseType = this.selectedCourse?.type;
    return [KEYS.CONTENT_TYPE.COURSE_TEMPLATE, KEYS.CONTENT_TYPE.CST].includes(courseType);
  }

  public goBack() {
    this.$emit('goBack');
  }

  public save() {
    let data: any = {}
    this.emitOnRotatePages();
  }

  cancel() {
    this.rotateDegreesInfo = [];
    this.emitOnRotatePages();
  }

  handleImageLoaded(index: number) {
    this.pagesLoaded++;
    const refs = this.$refs['PreviewFilePage'];
    const canvaSize = this.getBiggerBoxSize();
    if(Array.isArray(refs)) {
      const filteredRefs = refs
      .filter((el: Vue) => el.$el.querySelector('img')?.naturalWidth > 0);

      filteredRefs
      .forEach(el => {
        (el as any).setCanvasSize(canvaSize.width, canvaSize.height);
      })
    }
  }

  getBiggerBoxSize() {
    const boxes = document.querySelectorAll<HTMLImageElement>('.pdf-container.position-relative  img')
    const biggerBox: HTMLImageElement = Array.from(boxes)
    .filter((el: HTMLImageElement) => el.complete)
    .sort((a, b) => {
      return (a.naturalWidth * a.naturalHeight) - (b.naturalWidth * b.naturalHeight)
    })[0]
    const width = (biggerBox.naturalHeight / biggerBox.naturalWidth) * (biggerBox.naturalHeight);
    const height = biggerBox.naturalHeight;

    return { 
      width: width > biggerBox.naturalWidth ? width : biggerBox.naturalWidth,
      height
    }
  }

  onPageChanged(pageInfo: any) {
    let objIndex = this.rotateDegreesInfo.find(e => e.page == pageInfo.page);
    if (objIndex) {
      if(pageInfo.rotateDegrees > 0) {
         objIndex.rotateDegrees = pageInfo.rotateDegrees;
      } else {
        this.rotateDegreesInfo = this.rotateDegreesInfo.filter(e => e != objIndex);
      }
    } else {
      this.rotateDegreesInfo.push(pageInfo);
    }
  }

  async startRenderPages() {
    this.pages = await this.PreviewFilePagesProvider.setPdfPagesFromUrlFile(this.url);
    this.pdfStatus.setLoad();
  }

  onPageToGoChanged(event: any) {
    event.stopPropagation();
    const cleaned = this.pageToGo.replace(/\D/g, '');
   
    this.pageToGo = cleaned;
  }

  async goToPage() {
    if (this.pageToGo) {
      const pageNumber = Number(this.pageToGo) - 1;

      if (pageNumber < this.numberPages) {
        const item = this.$el.querySelector(`[id="${pageNumber}select"]`);
        item.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }); 
      }
    }
  }

  /* Hooks */
  async mounted() {
    try {
      this.pdfStatus.setLoading();
      this.rotateDegreesInfo = this.rotateDegrees || [];
      this.isRendered = true;
      await this.startRenderPages();
      if(this.params.edition) {
        this.startPage = Number(this.params.start) - 1;
        this.endPage = this.params.end ? Number(this.params.end) - 1 : null;
      }
    } catch(err) {
      this.pdfStatus.setError();
    }    
  }

  @Watch('pdfStatus', {
    deep: true
  })
  onPdfStatusChange(val) {
    if(!val.loading) {
      setTimeout(() => {
        document.getElementById('select-pages').scrollTo({ left: 0 })
      }, 100)
    }
  }
}